import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/global/PageLayout';
import { PageIntro, Section } from '../components/ui/PageIntro';
import { DefinitionList } from '../components/ui/DefinitionList';
import { Prose } from '../components/ui/Prose';
import { LegalNav } from '../components/legal/LegalNav';
import { BRAND, CONTACT, GBP_ADDRESS, LEGAL_ENTITY } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const linkClass = 'font-bold text-rust underline decoration-rust/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2';

/**
 * The statutory disclosure on its own page.
 *
 * The business is a SOLE TRADER, so there is no registered company name,
 * company number or registered office to publish. What the law does
 * require — the Business Names provisions of the Companies Act 2006,
 * because a name other than the proprietor's own is being used — is the
 * proprietor's name and an address for service. Both are here.
 *
 * The page therefore states the structure plainly rather than leaving a
 * reader to wonder why the company number is missing. Registry D40.
 */
export function CompanyDetails() {
  useDocumentMeta({
    title: 'Business details | Low On Fuel',
    description: `Who runs ${BRAND.name}, the business address and how to get in touch.`,
    path: '/legal/company-details'
  });
  return <PageLayout data-id="element-466" data-mp="dr173cnv5fah">
      <PageIntro eyebrow="Legal" title="Business details" intro="Who we are on paper, and how to get hold of us." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Legal',
      to: '/legal/privacy'
    }, {
      label: 'Business details'
    }]} data-id="element-467" data-mp="dqhzaorxg7a6" />

      <Section labelledBy="company-details-heading" data-id="element-468" data-mp="234msz731mwt4">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-469" data-mp="233bnn2ty9gzw">
          <div className="lg:col-span-8" data-id="element-470" data-mp="1y74gx7tqc1gw">
            <h2 id="company-details-heading" className="font-bebas text-4xl leading-none tracking-wide text-ink" data-id="element-471" data-mp="3smte6tdoe47q">
              Who runs the café
            </h2>

            <Prose className="mt-4" data-id="element-472" data-mp="3iox9okf8u7il">
              <p data-id="element-473" data-mp="3ipyisjcp8jxo">
                Low On Fuel Café is run by{' '}
                <strong className="font-bold text-ink" data-id="element-474" data-mp="3nn73orp8f22m">
                  {LEGAL_ENTITY.proprietorName}
                </strong>{' '}
                as a{' '}
                <strong className="font-bold text-ink" data-id="element-475" data-mp="f5u5chtlj7ho">sole trader</strong>,
                not a limited company. That means there is no registered
                company name or company number to give you. The person
                legally responsible is Andrew, and the business address below
                is where anything formal should be sent.
              </p>
              <p data-id="element-476" data-mp="2qwoerkz2xn84">
                The address is written here the way it appears on our Google
                listing, using Alfreton as the post town. Elsewhere on the
                site we say Brackenfield, because that is the village the café
                actually sits in. Both will get post and a satnav to the same
                door.
              </p>
            </Prose>

            <div className="mt-6" data-id="element-477" data-mp="2xcoiujnea4jc">
              <DefinitionList items={[{
              term: 'Trading name',
              value: BRAND.name
            }, {
              term: 'Proprietor',
              value: LEGAL_ENTITY.proprietorName
            }, {
              term: 'Business structure',
              value: 'Sole trader (no company registration)'
            }, {
              /* The Google Business Profile form of the address, at
                 the client's instruction, so the formal record here
                 matches the listing someone might check it against.
                 Every other page says Brackenfield, which is the
                 village; Alfreton is the post town. Registry D42. */
              term: 'Business address',
              value: GBP_ADDRESS
            }, {
              term: 'General enquiries',
              value: <a href={`mailto:${CONTACT.email}`} className={linkClass} data-id="element-479" data-mp="t6y580e1x3uh">
                        {CONTACT.email}
                      </a>
            }, {
              term: 'Andrew directly',
              value: <a href={`mailto:${CONTACT.proprietorEmail}`} className={linkClass} data-id="element-480" data-mp="1c0k8dk2xzrey">
                        {CONTACT.proprietorEmail}
                      </a>
            }, {
              term: 'Telephone',
              value: <a href={CONTACT.telephoneHref} className={linkClass} data-id="element-481" data-mp="3b6plewe6lrvb">
                        {CONTACT.telephone}
                      </a>
            }, {
              term: 'Website',
              value: BRAND.domain
            }]} data-id="element-478" data-mp="2xbn86gaajk25" />
            </div>

            <Prose className="mt-8" data-id="element-482" data-mp="2whtdr0g1egv8">
              <p data-id="element-483" data-mp="2wiw4n8dd0jb5">
                Please send general enquiries to{' '}
                <a href={`mailto:${CONTACT.email}`} className={linkClass} data-id="element-484" data-mp="3u5bnoelfpp0t">
                  {CONTACT.email}
                </a>{' '}
                rather than to Andrew, and you will get a faster answer.
              </p>
              <p data-id="element-485" data-mp="3k5ayic0vweop">
                For how we look after personal data, see our{' '}
                <Link to="/legal/privacy" className={linkClass} data-id="element-486" data-mp="3g48z9v6yw2mq">
                  privacy policy
                </Link>
                . For the terms on which you reserve something from the shop,
                see our{' '}
                <Link to="/legal/terms" className={linkClass} data-id="element-487" data-mp="24ou2arerdarm">
                  terms of use
                </Link>
                .
              </p>
            </Prose>
          </div>

          <aside className="lg:col-span-4 lg:col-start-9 lg:row-start-1" data-id="element-488" data-mp="2b5vjs17kaqnq">
            <LegalNav data-id="element-489" data-mp="1w9wgr4zb7gpg" />
          </aside>
        </div>
      </Section>
    </PageLayout>;
}